import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChartBar, ClockCounterClockwise, FloppyDisk, Flask, Key, Microphone, SlidersHorizontal, Users } from "@phosphor-icons/react";
import { AppHeader } from "@/components/observer/AppHeader";
import { OverviewPanel } from "@/components/admin/OverviewPanel";
import { UsersPanel } from "@/components/admin/UsersPanel";
import { ActivityPanel } from "@/components/admin/ActivityPanel";
import { TestsPanel } from "@/components/admin/TestsPanel";
import { KeysPanel } from "@/components/admin/KeysPanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { SessionsPanel } from "@/components/admin/SessionsPanel";
import { api, errMsg } from "@/lib/api";
import { btnPrimary, labelCls } from "@/lib/ui";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: ChartBar, title: "Overview." },
  { id: "people", label: "People", icon: Users, title: "People." },
  { id: "meetings", label: "Meetings", icon: Microphone, title: "Meetings." },
  { id: "settings", label: "Settings", icon: SlidersHorizontal, title: "Instruments." },
  { id: "keys", label: "API keys", icon: Key, title: "API keys." },
  { id: "tests", label: "Tests", icon: Flask, title: "Tests." },
  { id: "activity", label: "Activity", icon: ClockCounterClockwise, title: "Activity." },
];

// Settings and keys share one config load, so a saved key can refresh which engines are ready.
function useConfig(enabled) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/config");
    setCfg(data);
    setDraft(data.settings);
  }, []);
  useEffect(() => { if (enabled && !cfg) load().catch((e) => toast.error(errMsg(e))); }, [enabled, cfg, load]);
  return { cfg, setCfg, draft, setDraft };
}

const SettingsSection = ({ cfg, setCfg, draft, setDraft }) => {
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg.settings);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", { settings: draft });
      setCfg((c) => ({ ...c, settings: data.settings }));
      setDraft(data.settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const reset = async (name) => {
    const { data } = await api.post("/admin/config/reset", { name });
    setCfg((c) => ({ ...c, settings: data.settings }));
    setDraft((d) => ({ ...d, [name]: data.settings[name] }));
  };
  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-20 -mx-1 px-1 py-2 bg-[var(--o-bg)] flex items-center justify-between gap-3">
        <p className="text-xs text-[var(--o-muted2)]">Applies to every meeting started after you save.</p>
        <button onClick={save} disabled={!dirty || saving} className={btnPrimary} data-testid="admin-save-button">
          <FloppyDisk size={15} /> {dirty ? "Save changes" : "Saved"}
        </button>
      </div>
      <SettingsPanel draft={draft} setDraft={setDraft} defaults={cfg.defaults} onReset={reset} cfg={cfg} />
    </div>
  );
};

const MeetingsSection = () => {
  const [sessions, setSessions] = useState(null);
  useEffect(() => { api.get("/admin/sessions").then(({ data }) => setSessions(data)).catch((e) => toast.error(errMsg(e))); }, []);
  return sessions ? <SessionsPanel sessions={sessions} /> : <div className="h-40 shimmer" />;
};

export default function Admin() {
  const { section } = useParams();
  const current = SECTIONS.find((s) => s.id === section);
  const needsConfig = section === "settings" || section === "keys";
  const { cfg, setCfg, draft, setDraft } = useConfig(needsConfig);
  // Leaving Settings with unsaved edits would lose them silently.
  const dirty = cfg && JSON.stringify(draft) !== JSON.stringify(cfg.settings);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // On a phone the sections scroll sideways; keep the open one in view.
  const navRef = useRef(null);
  useEffect(() => {
    navRef.current?.querySelector("[aria-current=page]")?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [section]);

  if (!current) return <Navigate to="/admin/overview" replace />;

  // A saved key changes which engines and voices are ready; refresh those without touching unsaved edits.
  const keysSaved = async (keys) => {
    setCfg((c) => ({ ...c, keys }));
    const { data } = await api.get("/admin/config").catch(() => ({ data: null }));
    if (data) setCfg((c) => ({ ...c, keys: data.keys, engines: data.engines, voices: data.voices, providers: data.providers }));
  };

  const navCls = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 text-sm whitespace-nowrap border-b-2 md:border-b-0 md:border-l-2 transition-colors ${
      isActive ? "border-[var(--o-fg)] text-[var(--o-fg)] font-semibold bg-[var(--o-surface)]" : "border-transparent text-[var(--o-muted)] hover:text-[var(--o-fg)]"
    }`;

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="admin-page">
      <AppHeader />
      <main className="relative z-10 max-w-7xl px-4 sm:px-10 pb-32">
        <div className="pt-10 pb-6 rise">
          <p className={labelCls}>Admin</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3" data-testid="admin-title">{current.title}</h1>
        </div>
        <div className="md:grid md:grid-cols-[180px_1fr] md:gap-8">
          <nav ref={navRef} className="flex md:flex-col overflow-x-auto md:overflow-visible border-b md:border-b-0 border-[var(--o-border)] mb-6 md:mb-0 md:sticky md:top-4 md:self-start" aria-label="Admin sections" data-testid="admin-nav">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <NavLink key={id} to={`/admin/${id}`} className={navCls} data-testid={`admin-nav-${id}`}>
                <Icon size={15} /> {label}
              </NavLink>
            ))}
          </nav>
          <section className="min-w-0" data-testid={`admin-section-${section}`}>
            {section === "overview" && <OverviewPanel />}
            {section === "people" && <UsersPanel />}
            {section === "meetings" && <MeetingsSection />}
            {section === "activity" && <ActivityPanel />}
            {section === "tests" && <TestsPanel />}
            {needsConfig && !cfg && <div className="h-40 shimmer" />}
            {section === "settings" && cfg && <SettingsSection cfg={cfg} setCfg={setCfg} draft={draft} setDraft={setDraft} />}
            {section === "keys" && cfg && <KeysPanel keys={cfg.keys} onSaved={keysSaved} />}
          </section>
        </div>
      </main>
    </div>
  );
}
